/**
 * Import/Export Page
 * Import and export diagnostic questions
 */

import { createFileRoute } from '@tanstack/react-router';
import { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, Download, Upload, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useExportQuestions, useImportQuestions, useRecalculateAll } from '@/hooks/use-admin-diagnostic';

export const Route = createFileRoute('/admin/diagnostic/import-export')({
  component: ImportExport,
});

function ImportExport() {
  const [importMode, setImportMode] = useState<'append' | 'replace'>('append');
  const [importFile, setImportFile] = useState<File | null>(null);

  const exportQuestions = useExportQuestions();
  const importQuestions = useImportQuestions();
  const recalculateAll = useRecalculateAll();

  const handleExport = async () => {
    try {
      const blob = await exportQuestions.mutateAsync();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `diagnostic-questions-${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      toast.warning('Please select a file');
      return;
    }

    try {
      const text = await importFile.text();
      const data = JSON.parse(text);

      if (!data.questions || !Array.isArray(data.questions)) {
        toast.error('Invalid file format. Expected { questions: [...] }');
        return;
      }

      const result = await importQuestions.mutateAsync({
        questions: data.questions,
        mode: importMode,
      });

      toast.success(`Import complete! Imported: ${result.imported}, Errors: ${result.errors.length}`);

      if (result.errors.length > 0) {
        console.error('Import errors:', result.errors);
      }

      setImportFile(null);
    } catch (error) {
      toast.error('Import failed: ' + (error instanceof Error ? error.message : String(error)));
    }
  };

  const [showRecalcConfirm, setShowRecalcConfirm] = useState(false);

  const handleRecalculateAll = useCallback(async () => {
    setShowRecalcConfirm(false);
    try {
      const result = await recalculateAll.mutateAsync();
      toast.success(`Recalculation complete! Total: ${result.total}, Recalculated: ${result.recalculated}, Errors: ${result.errors.length}`);

      if (result.errors.length > 0) {
        console.error('Recalculation errors:', result.errors);
      }
    } catch (error) {
      toast.error('Recalculation failed: ' + (error instanceof Error ? error.message : String(error)));
    }
  }, [recalculateAll]);

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Import/Export</h1>
        <p className="text-muted-foreground mt-1">
          Manage diagnostic questions data
        </p>
      </div>

      <div className="space-y-6">
        {/* Export */}
        <Card>
          <CardHeader>
            <CardTitle>Export Questions</CardTitle>
            <CardDescription>
              Download all active diagnostic questions as JSON
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={handleExport}
              disabled={exportQuestions.isPending}
              className="gap-2"
            >
              {exportQuestions.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  Export Questions
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Import */}
        <Card>
          <CardHeader>
            <CardTitle>Import Questions</CardTitle>
            <CardDescription>
              Upload a JSON file to import questions
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Mode Selection */}
            <div className="space-y-2">
              <Label htmlFor="import-mode">Import Mode</Label>
              <Select
                value={importMode}
                onValueChange={(value: any) => setImportMode(value)}
              >
                <SelectTrigger id="import-mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="append">Append (Add new questions)</SelectItem>
                  <SelectItem value="replace">Replace (Deactivate existing, add new)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {importMode === 'append'
                  ? 'New questions will be added alongside existing questions'
                  : 'All existing questions will be marked inactive and replaced'}
              </p>
            </div>

            {/* File Upload */}
            <div className="space-y-2">
              <Label htmlFor="import-file">Select JSON File</Label>
              <input
                id="import-file"
                type="file"
                accept=".json"
                onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                className="block w-full text-sm text-muted-foreground file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-primary-foreground hover:file:bg-primary/90"
              />
              {importFile && (
                <p className="text-xs text-muted-foreground">
                  Selected: {importFile.name} ({(importFile.size / 1024).toFixed(2)} KB)
                </p>
              )}
            </div>

            {/* Warning */}
            {importMode === 'replace' && (
              <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
                <div className="text-sm text-yellow-800">
                  <p className="font-medium">Warning: Replace Mode</p>
                  <p className="mt-1">
                    All existing questions will be marked as inactive. This action cannot be undone easily.
                  </p>
                </div>
              </div>
            )}

            <Button
              onClick={handleImport}
              disabled={!importFile || importQuestions.isPending}
              className="gap-2"
            >
              {importQuestions.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  Import Questions
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Recalculate All */}
        <Card className="border-orange-200">
          <CardHeader>
            <CardTitle>Recalculate All Organisations</CardTitle>
            <CardDescription>
              Recalculate Prime Scores for all organisations using latest formulas
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-2 p-3 bg-orange-50 border border-orange-200 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-orange-600 mt-0.5" />
              <div className="text-sm text-orange-800">
                <p className="font-medium">Use with Caution</p>
                <p className="mt-1">
                  This will recalculate all organisation Prime Scores. Only use this after
                  updating question weights or scoring formulas.
                </p>
              </div>
            </div>

            <Button
              onClick={() => setShowRecalcConfirm(true)}
              disabled={recalculateAll.isPending}
              variant="destructive"
              className="gap-2"
            >
              {recalculateAll.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Recalculating...
                </>
              ) : (
                'Recalculate All Prime Scores'
              )}
            </Button>

            <AlertDialog open={showRecalcConfirm} onOpenChange={setShowRecalcConfirm}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Recalculate All Prime Scores?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will recalculate Prime Scores for ALL organisations. This may take several minutes.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleRecalculateAll}>Continue</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
